import { FC, useMemo } from 'react';

import styled from '@emotion/styled';
import { CandidatePersonalDetails } from 'store/candidate/types';
import { ReadonlyField } from 'components';
import { formatDate } from 'core';
import { useGetAllCountriesQuery } from 'store/country/countryAPI';

import { Divider } from '@britishcouncil/react-solas-ors3';

export const ReadonlyCandidate: FC<{ candidate: CandidatePersonalDetails }> = ({ candidate }) => {
  const { data: countriesOptions } = useGetAllCountriesQuery();

  const countryName = useMemo(
    () => countriesOptions?.find((x) => x.id === candidate.address.countryId)?.name ?? '-',
    [candidate, countriesOptions]
  );

  return (
    <DetailsContainer>
      <StyledReadonlyField label="First Name">{candidate.otherNames}</StyledReadonlyField>
      <StyledReadonlyField label="Last Name">{candidate.familyName}</StyledReadonlyField>
      {!candidate.isChild && <StyledReadonlyField label="Email">{candidate.email}</StyledReadonlyField>}
      <StyledReadonlyField label="Date of Birth">{formatDate(candidate.dob)}</StyledReadonlyField>
      <StyledReadonlyField label="Gender">
        {candidate.gender === 'M' ? 'Male' : candidate.gender === 'F' ? 'Female' : ''}
      </StyledReadonlyField>
      <StyledReadonlyField label="Address Line 1">{candidate.address.streetAddress1}</StyledReadonlyField>
      <StyledReadonlyField label="Address Line 2">{candidate.address.streetAddress2}</StyledReadonlyField>
      <StyledReadonlyField label="Address Line 3">{candidate.address.streetAddress3}</StyledReadonlyField>
      <StyledReadonlyField label="Address Line 4">{candidate.address.streetAddress4}</StyledReadonlyField>
      <StyledReadonlyField label="City">{candidate.address.city}</StyledReadonlyField>
      <StyledReadonlyField label="PostCode">{candidate.address.postCode}</StyledReadonlyField>
      <StyledReadonlyField label="Country/Territory">{countryName}</StyledReadonlyField>
      {!candidate.isChild && (
        <>
          <StyledReadonlyField label="Alternative contact number">{candidate.telephone}</StyledReadonlyField>
          <StyledReadonlyField label="Mobile number">{candidate.mobile}</StyledReadonlyField>
        </>
      )}
      <StyledReadonlyField label="UCI Edexcel">{candidate.uciEdexcel}</StyledReadonlyField>
      <StyledReadonlyField label="UCI AQA">{candidate.uciAqa}</StyledReadonlyField>

      {candidate.isChild && (
        <>
          <Divider />
          <h2>Parent/guardian contact details:</h2>
          <StyledReadonlyField label="First Name">{candidate.guardianContactDetails?.firstName}</StyledReadonlyField>
          <StyledReadonlyField label="Last Name">{candidate.guardianContactDetails?.lastName}</StyledReadonlyField>
          <StyledReadonlyField label="Email">{candidate.guardianContactDetails?.email}</StyledReadonlyField>
          <StyledReadonlyField label="Relationship to the child">
            {candidate.guardianContactDetails?.familyRelationship}
          </StyledReadonlyField>
          <StyledReadonlyField label="Date of Birth">
            {candidate.guardianContactDetails?.dateOfBirth}
          </StyledReadonlyField>
          <StyledReadonlyField label="Alternative contact number">
            {candidate.guardianContactDetails?.telephone}
          </StyledReadonlyField>
          <StyledReadonlyField label="Mobile number">{candidate.guardianContactDetails?.mobile}</StyledReadonlyField>
        </>
      )}
    </DetailsContainer>
  );
};

const DetailsContainer = styled.div`
  margin-top: 1em;
`;

const StyledReadonlyField = styled(ReadonlyField)`
  dt {
    width: 290px;
  }
`;
